import { SignUpForm } from "./SignUpForm";
import { SmartScribe } from "@/components/Icons/Icons";
import { TenantImage } from "@/components/Images/TenantImage";
import { PageHelmet } from "@/components/PageHelmet/PageHelmet";
import { AppText } from "@/components/Text/AppText";
import { translate } from "@/helper/translate";
import { useAppDispatch, useAppSelector } from "@/hooks/types";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { useTenantFeature } from "@/hooks/useTenantFeature";
import { tenantNavigate } from "@/routes/utils/tenantNavigate";
import { getIs1TemplateTenant } from "@/store/tenant/selectors";
import {
    setAccessToken,
    setRefreshToken,
    setUserDetails,
} from "@/store/user/slice";
import { User } from "@/types";
import { AppRoutes } from "@/types/routes";
import { ITenantFeature } from "@/types/tenant";
import { Tokens } from "@/types/token";
import { FileText, LayoutPanelLeft, Paintbrush } from "lucide-react";
import { useNavigate } from "react-router-dom";

export const SignUpPage = () => {
    const [, setLocalAccessToken] = useLocalStorage("accessToken", null);
    const [, setLocalRefreshToken] = useLocalStorage("refreshToken", null);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isPrefWizardEnabled = useTenantFeature(
        ITenantFeature.PreferencesWizard,
    );
    const is1TemplateTenant = useAppSelector(getIs1TemplateTenant);

    const onLoginSuccess = (user: User, tokens: Tokens) => {
        if (user.isEmailVerified) {
            dispatch(setAccessToken(tokens.access));
            dispatch(setRefreshToken(tokens.refresh));
            setLocalAccessToken(tokens.access);
            setLocalRefreshToken(tokens.refresh);
            dispatch(setUserDetails(user));
            const hasAPreferenceSet =
                (user.preferences?.jobTypes ?? []).length > 0 ||
                (user.preferences?.industries ?? []).length > 0 ||
                (user.preferences?.jobRoles ?? []).length > 0 ||
                !!user.preferences?.experienceLevel;

            tenantNavigate({
                navigate,
                isFeatureEnabled: !hasAPreferenceSet && isPrefWizardEnabled,
                enabledTo: `/${AppRoutes.Preferences}`,
                disabledTo: "/",
                navigateOptions: {
                    replace: true,
                },
            });
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            {is1TemplateTenant ? (
                <div className="mx-auto flex min-h-screen w-full items-center justify-center lg:flex">
                    {/* Left column - Form */}
                    <div className="flex min-h-screen flex-1 flex-col items-center justify-center bg-white lg:min-w-[500px]">
                        <div className="mx-auto flex max-h-[830px] w-full flex-col items-center px-6 py-8 lg:px-8 lg:py-6 xl:px-16 xl:py-8">
                            {/* Mobile Logo */}
                            <div className="mb-6 flex justify-center lg:hidden">
                                <TenantImage
                                    imageType="primaryLogo"
                                    className="h-10 w-auto"
                                />
                            </div>

                            <PageHelmet
                                title="Sign up"
                                description="Create Account"
                                robotsOverride="noindex, nofollow"
                                url={`${window.location.origin}/${AppRoutes.Signup}`}
                            />

                            <div className="lg:hidden">
                                <AppText
                                    variant="headings"
                                    className="text-center text-neutral-900"
                                >
                                    GET STARTED FOR FREE
                                </AppText>
                                <AppText
                                    variant="regular"
                                    className="mt-2 text-center !font-medium uppercase text-neutral-600"
                                >
                                    Unlock the full potential of your resume
                                    today
                                </AppText>
                            </div>

                            <div className="hidden text-center lg:block">
                                <AppText
                                    variant="headings"
                                    className="text-neutral-900"
                                >
                                    GET STARTED FOR FREE
                                </AppText>
                                <AppText
                                    variant="regular"
                                    className="mt-2 !font-medium uppercase text-neutral-600"
                                >
                                    Unlock the full potential of your resume
                                    today
                                </AppText>
                            </div>

                            <SignUpForm onSuccess={onLoginSuccess} />
                        </div>
                    </div>
                    {/* Right column - Hero/Benefits */}
                    <div className="relative hidden min-h-screen bg-gradient-to-bl from-primary-700 to-primary-900 lg:flex lg:w-[55%] xl:w-[55%]">
                        <div className="flex w-full items-center justify-center">
                            <div className="relative flex h-full w-full flex-col p-8 text-white lg:h-[830px] xl:px-12 xl:py-8">
                                <TenantImage
                                    imageType="footerLogo"
                                    className="h-10 w-auto"
                                />
                                <div className="mx-auto mt-12 flex max-w-2xl flex-1 flex-col">
                                    <AppText
                                        variant="headings"
                                        className="mb-4 uppercase text-white"
                                    >
                                        Elevate Your Career with a Resume That
                                        Gets Results
                                    </AppText>
                                    <AppText
                                        variant="regular"
                                        className="mb-8 text-white/90"
                                    >
                                        Build your resume in minutes with the
                                        most advanced AI-powered builder.
                                    </AppText>

                                    <div className="mt-4 space-y-6">
                                        <div className="flex items-start">
                                            <div className="mr-4 flex h-10 w-10 items-center justify-center">
                                                <SmartScribe className="h-6 w-6 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <AppText
                                                    variant="contextheading"
                                                    className="font-semibold text-white"
                                                >
                                                    Smart Tools for Faster
                                                    Results
                                                </AppText>
                                                <AppText
                                                    variant="regular"
                                                    className="text-white/80"
                                                >
                                                    Build powerful resumes in
                                                    seconds with Smart Scribe
                                                    and Smart Match AI.
                                                </AppText>
                                            </div>
                                        </div>
                                        <div className="flex items-start">
                                            <div className="mr-4 flex h-10 w-10 items-center justify-center">
                                                <FileText className="h-5 w-5 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <AppText
                                                    variant="contextheading"
                                                    className="font-semibold text-white"
                                                >
                                                    Expert-Designed Templates
                                                </AppText>
                                                <AppText
                                                    variant="regular"
                                                    className="text-white/80"
                                                >
                                                    Stand out with templates
                                                    built for impact —
                                                    professional, polished, and
                                                    ATS-friendly.
                                                </AppText>
                                            </div>
                                        </div>

                                        <div className="flex items-start">
                                            <div className="mr-4 flex h-10 w-10 items-center justify-center">
                                                <LayoutPanelLeft className="h-5 w-5 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <AppText
                                                    variant="contextheading"
                                                    className="font-semibold text-white"
                                                >
                                                    Dynamic Formatting
                                                </AppText>
                                                <AppText
                                                    variant="regular"
                                                    className="text-white/80"
                                                >
                                                    Drag, drop, and done — our
                                                    system handles the layout
                                                    for you.
                                                </AppText>
                                            </div>
                                        </div>

                                        <div className="flex items-start">
                                            <div className="mr-4 flex h-10 w-10 items-center justify-center">
                                                <Paintbrush className="h-5 w-5 text-white" />
                                            </div>
                                            <div className="flex-1">
                                                <AppText
                                                    variant="contextheading"
                                                    className="font-semibold text-white"
                                                >
                                                    Instant Design
                                                </AppText>
                                                <AppText
                                                    variant="regular"
                                                    className="text-white/80"
                                                >
                                                    One-click styling for{" "}
                                                    {translate("colours")},
                                                    fonts, and layouts — no more
                                                    fighting with editors.
                                                </AppText>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Testimonial */}
                                <div className="mx-auto mt-auto  w-full max-w-2xl">
                                    <div className="rounded-lg bg-white/10 p-6 backdrop-blur-sm">
                                        <div className="flex items-center">
                                            <div className="mr-3 h-20 w-20 overflow-hidden rounded-full border-2 border-white/30">
                                                <img
                                                    src="https://framerusercontent.com/images/0LRBZkUXkwuF2WcmgGx1YS4o.jpeg?scale-down-to=1024"
                                                    alt="User testimonial"
                                                    className="h-full w-full object-cover"
                                                />
                                            </div>
                                            <div className="flex items-baseline gap-2">
                                                <AppText
                                                    variant="contextheading"
                                                    className="font-semibold text-white"
                                                >
                                                    Monica Candy
                                                </AppText>
                                                <AppText
                                                    variant="footnote"
                                                    className="text-white/80"
                                                >
                                                    MBA Graduate
                                                </AppText>
                                            </div>
                                        </div>
                                        <AppText
                                            variant="regular"
                                            className="mt-3 italic text-white/90"
                                        >
                                            &ldquo;1Template is an absolute
                                            game-changer! I quickly created
                                            multiple versions of my resume for
                                            each job I applied for. It&apos;s an
                                            essential tool for anyone looking to
                                            stand out in their
                                            applications.&rdquo;
                                        </AppText>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // Other tenants: centered card layout
                <div className="min-h-screen bg-gradient-to-bl from-primary-700 to-primary-900 py-4 pb-20 lg:pb-4">
                    <div className="relative z-10 mx-auto flex min-h-screen max-w-lg items-center justify-center px-6 lg:max-w-xl">
                        <div className="w-full rounded-xl bg-white p-8 shadow-xl">
                            {/* Logo */}
                            <div className="mb-8 flex justify-center">
                                <TenantImage
                                    imageType="primaryLogo"
                                    className="h-10 w-auto"
                                />
                            </div>

                            <PageHelmet
                                title="Sign up"
                                description="Create Account"
                                robotsOverride="noindex, nofollow"
                                url={`${window.location.origin}/${AppRoutes.Signup}`}
                            />

                            <div className="mb-4 text-center">
                                <AppText
                                    variant="headings"
                                    className="text-neutral-900"
                                >
                                    GET STARTED FOR FREE
                                </AppText>
                            </div>

                            <SignUpForm onSuccess={onLoginSuccess} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
